import { useState, useEffect, useCallback, useRef } from 'react';
import { IS_WEB } from '../../shared/platform';

/**
 * Simple hook to manage multiple browser tabs for live sessions.
 * When multiple tabs are detected, shows a modal to let user choose which tab to keep.
 * Other tabs are redirected to the homepage.
 *
 * @param {string|number} sessionId - The live session ID
 * @returns {Object} Tab management state and actions
 */
export const useTabManagement = (sessionId) => {
  const [showKeepTabModal, setShowKeepTabModal] = useState(false);
  // Exposé en state (et pas seulement en ref) : la reconnexion automatique en a
  // besoin pour savoir quand elle a de nouveau le droit de reprendre la main —
  // après une éviction par un autre onglet, elle doit rester muette tant qu'il
  // est là, sinon les deux onglets s'éjectent en boucle.
  const [hasOtherTabs, setHasOtherTabs] = useState(false);

  const tabIdRef = useRef(`tab_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`);
  const channelRef = useRef(null);
  const hasOtherTabsRef = useRef(false);
  // Cet onglet est en train de partir : il ne doit plus se déclarer vivant.
  const isLeavingRef = useRef(false);

  // Un seul ecrivain pour le couple ref + state : ecrits en miroir a la main,
  // tout site futur qui en oublie un casse silencieusement la reconnexion.
  const markOtherTabs = useCallback((present) => {
    hasOtherTabsRef.current = present;
    setHasOtherTabs(present);
  }, []);

  useEffect(() => {
    if (!IS_WEB || !sessionId) return;

    if (typeof BroadcastChannel === 'undefined') return;

    // Remis a zero a chaque (re)montage : le nettoyage precedent l'a passe a
    // true. Sans ca, apres un changement de seance cet onglet ne repondrait
    // plus jamais au PING, et les autres se croiraient seuls a tort.
    isLeavingRef.current = false;

    const tabId = tabIdRef.current;
    const channelName = `masteur_live_${sessionId}`;
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const { type, fromTabId } = event.data;
      if (fromTabId === tabId) return;

      switch (type) {
        case 'TAB_JOINED':
          // Another tab joined - show modal and announce our presence
          markOtherTabs(true);
          setShowKeepTabModal(true);
          channel.postMessage({ type: 'TAB_EXISTS', fromTabId: tabId });
          break;

        case 'TAB_EXISTS':
          // Another tab exists - show modal
          markOtherTabs(true);
          setShowKeepTabModal(true);
          break;

        case 'CLOSE_TABS':
          // Another tab wants to be the active one - redirect this tab
          setShowKeepTabModal(false);
          window.location.replace('https://fr.masteur.com/');
          break;

        case 'TAB_LEFT':
          // A tab closed - check if we're alone now
          hasOtherTabsRef.current = false;
          channel.postMessage({ type: 'PING', fromTabId: tabId });
          setTimeout(() => {
            if (!hasOtherTabsRef.current) {
              setShowKeepTabModal(false);
              markOtherTabs(false);
            }
          }, 500);
          break;

        case 'PING':
          // Un onglet vérifie s'il reste seul. S'il nous répond alors qu'on est
          // déjà en train de se fermer, il conclut à tort qu'il a de la
          // compagnie — et il le croira pour toujours, puisque plus personne ne
          // le détrompera. C'est ce qui laissait la modale ouverte et, depuis
          // que la reconnexion s'y adosse, la room définitivement morte.
          if (isLeavingRef.current) break;
          markOtherTabs(true);
          channel.postMessage({ type: 'PONG', fromTabId: tabId });
          break;

        case 'PONG':
          // Response to our ping - other tabs still exist
          markOtherTabs(true);
          break;

        default:
          break;
      }
    };

    // Announce this tab joined
    channel.postMessage({ type: 'TAB_JOINED', fromTabId: tabId });

    // Notify others when leaving
    const handleBeforeUnload = () => {
      isLeavingRef.current = true;
      channel.postMessage({ type: 'TAB_LEFT', fromTabId: tabId });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      isLeavingRef.current = true;
      channel.postMessage({ type: 'TAB_LEFT', fromTabId: tabId });
      channel.close();
    };
  }, [sessionId, markOtherTabs]);

  // Handle "Keep this tab" button click
  const handleKeepThisTab = useCallback(() => {
    if (!IS_WEB || !channelRef.current) return;

    // Tell other tabs to redirect
    channelRef.current.postMessage({
      type: 'CLOSE_TABS',
      fromTabId: tabIdRef.current
    });

    // Hide modal after a short delay
    setTimeout(() => {
      setShowKeepTabModal(false);
      // Cet onglet est celui qu'on garde : la reconnexion peut reprendre.
      markOtherTabs(false);
    }, 500);
  }, [markOtherTabs]);

  return {
    showKeepTabModal,
    handleKeepThisTab,
    hasOtherTabs,
    isWeb: IS_WEB,
  };
};

export default useTabManagement;
